import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FlatList, StyleSheet, View } from 'react-native'
import { useFocusEffect, useRouter } from 'expo-router'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { DS } from '@/design-system'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'
import { toastNotificationStore } from '@/components/ANewDesign/ToastNotification/ToastNotification'
import { TournamentTableCard } from '../TournamentTableCard'
import { useMyTournamentTable } from '../useMyTournamentTable'

interface Props {
  tournamentId: Id<'tournaments'>
}

type TableRow = NonNullable<ReturnType<typeof useQuery<typeof api.tournaments.queries.getTournamentTables>>>[number]
type StatusRow = NonNullable<ReturnType<typeof useQuery<typeof api.tournaments.queries.getTournamentStatus>>>

/**
 * Inline tables overview for the lobby tabs. Previously a standalone screen;
 * now embedded so navigation between tabs is in-place.
 *
 * Eliminated users still get a per-table "Observe" button which routes
 * to `/game/[gameId]` to spectate.
 *
 * Uses `FlatList` rather than `ScrollView` so a tournament with many tables
 * doesn't pay the mount cost for every card up front. Header / footer slots
 * carry the summary card, my-table card, and empty-state messaging.
 */
export function TournamentTablesView({ tournamentId }: Props) {
  const router = useRouter()
  const store = useStore()
  const { user, isAdmin, isSuperAdmin } = store.session
  const { setEvent } = store.analytics
  const userId = user?.auth0Id || ''
  const canSeeBotBreakdown = isAdmin || isSuperAdmin

  const tables = useQuery(api.tournaments.queries.getTournamentTables, { tournamentId })
  const status = useQuery(api.tournaments.queries.getTournamentStatus, { tournamentId })
  const myTable = useMyTournamentTable(tournamentId)

  const spectate = useMutation(api.games.mutations.spectateGame)
  const isEliminated = myTable?.tournamentEliminated === true
  const myTableNumber = myTable?.tableNumber ?? null

  const [spectatingGameId, setSpectatingGameId] = useState<string | null>(null)

  // Clear the in-flight spectate flag whenever the lobby route regains focus.
  // We set `spectatingGameId` on tap and never clear it on the success path —
  // navigating to `/game/[gameId]` is fire-and-forget. Because this view stays
  // mounted under the Tables tab (it's swapped content, not a pushed route),
  // returning to the lobby would otherwise leave every Observe button disabled
  // until the user manually re-mounted by switching tabs. `useFocusEffect`
  // fires when the lobby route regains focus from the popped game route.
  useFocusEffect(
    useCallback(() => {
      setSpectatingGameId(null)
    }, [])
  )

  const tablesViewedRef = useRef(false)
  useEffect(() => {
    if (tablesViewedRef.current) return
    if (!tables || !status) return
    tablesViewedRef.current = true
    setEvent(events.mtt.tables_screen_viewed, {
      tournamentId,
      isEliminated,
      tableCount: tables.length,
      myTableNumber
    })
  }, [tables, status, isEliminated, myTableNumber, tournamentId, setEvent])

  const handleSpectate = useCallback(
    async (gameId: string) => {
      if (!userId || !isEliminated) return
      if (spectatingGameId) return
      setSpectatingGameId(gameId)
      setEvent(events.mtt.spectate_table_tapped, { tournamentId, gameId })
      try {
        await spectate({
          tournamentId,
          gameId: gameId as Id<'games'>,
          userId
        })
        // Fire-and-forget analytics for the success path. The previous version
        // only fired `spectate_table_tapped` on entry, so a backend failure
        // looked indistinguishable from a successful tap in the funnel.
        setEvent(events.mtt.spectate_table_succeeded, { tournamentId, gameId })
        // No URL flags — the client derives spectator status from server state
        // via `getMySpectatorStatus`, and the tournament link comes from
        // `game.tournamentId` directly.
        router.push({
          pathname: '/game/[gameId]',
          params: { gameId }
        } as never)
      } catch (err) {
        console.error('Failed to spectate:', err)
        setEvent(events.mtt.spectate_table_failed, {
          tournamentId,
          gameId,
          reason: err instanceof Error ? err.message : 'unknown'
        })
        toastNotificationStore.getState().show({
          text: err instanceof Error ? err.message : 'Could not start spectating. Please try again.'
        })
        setSpectatingGameId(null)
      }
    },
    [userId, isEliminated, spectatingGameId, setEvent, tournamentId, spectate, router]
  )

  const renderTable = useCallback(
    ({ item: table }: { item: TableRow }) => {
      const isMyCurrentTable = myTableNumber === table.tableNumber
      return (
        <TournamentTableCard
          tableNumber={table.tableNumber}
          playerCount={table.playerCount}
          humanCount={table.humanCount}
          botCount={table.botCount}
          averageStack={table.averageStack}
          largestStack={table.largestStack}
          smallestStack={table.smallestStack}
          handInProgress={table.handInProgress}
          communityCards={table.communityCards}
          pot={table.pot}
          isMyCurrentTable={isMyCurrentTable}
          showBotBreakdown={canSeeBotBreakdown}
          onObserve={isEliminated ? () => handleSpectate(table.gameId) : undefined}
          observeDisabled={spectatingGameId !== null}
        />
      )
    },
    [myTableNumber, canSeeBotBreakdown, isEliminated, spectatingGameId, handleSpectate]
  )

  // Memoize the FlatList header — re-creating it on every render would
  // re-mount the summary card chrome.
  const listHeader = useMemo(() => {
    if (!status) return null
    return (
      <TablesListHeader
        status={status}
        myTableNumber={myTableNumber}
        isEliminated={isEliminated}
      />
    )
  }, [status, myTableNumber, isEliminated])

  if (tables === undefined || status === undefined) {
    return (
      <View style={styles.centered}>
        <DS.Text variant="Body Large/Regular" color="Brand/Dark Grey">Loading tables…</DS.Text>
      </View>
    )
  }

  if (tables === null || status === null) {
    return (
      <View style={styles.centered}>
        <DS.Frame gap="12" style={{ padding: 24, alignItems: 'center' }}>
          <DS.Text variant="Heading 3/Medium">Tournament not found</DS.Text>
          <DS.Text variant="Body Large/Regular" style={{ textAlign: 'center', color: DS.palette['Brand/Dark Grey'] }}>
            This tournament may have ended or been removed.
          </DS.Text>
        </DS.Frame>
      </View>
    )
  }

  return (
    <FlatList
      data={tables}
      keyExtractor={(table) => table.gameId as string}
      renderItem={renderTable}
      ListHeaderComponent={listHeader}
      ListEmptyComponent={
        <DS.Text variant="Body Large/Regular" style={{ textAlign: 'center', color: '#888', paddingTop: 24 }}>
          No active tables
        </DS.Text>
      }
      contentContainerStyle={styles.content}
      ItemSeparatorComponent={() => <View style={styles.separator} />}
      // Bound the work on long fields. Most tournaments have ≤ 50 tables.
      initialNumToRender={12}
      maxToRenderPerBatch={12}
      windowSize={5}
      removeClippedSubviews
    />
  )
}

function TablesListHeader({
  status,
  myTableNumber,
  isEliminated
}: {
  status: StatusRow
  myTableNumber: number | null
  isEliminated: boolean
}) {
  return (
    <DS.Frame gap="16" style={styles.headerFrame}>
      <DS.Frame gap="4" style={styles.summaryCard}>
        <DS.Text variant="Body Large/Medium">
          {status.activePlayerCount} players across {status.activeTableCount} table
          {status.activeTableCount !== 1 ? 's' : ''}
        </DS.Text>
        <DS.Text variant="Mini/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
          Blinds: {status.currentSmallBlind}/{status.currentBigBlind}
        </DS.Text>
        {!isEliminated && (
          <DS.Text variant="Mini/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
            Read-only table overview while you are still in the tournament.
          </DS.Text>
        )}
      </DS.Frame>

      {!isEliminated && myTableNumber !== null && (
        <DS.Frame gap="4" style={styles.myTableSummaryCard}>
          <DS.Text variant="Mini/Bold" style={{ color: DS.palette['Brand/Maroon'] }}>
            Your Current Table
          </DS.Text>
          <DS.Text variant="Body Large/Medium">Table {myTableNumber}</DS.Text>
        </DS.Frame>
      )}
    </DS.Frame>
  )
}

const styles = StyleSheet.create({
  // Tighter top padding so the first table card sits close under the tab strip.
  content: { paddingHorizontal: 16, paddingTop: 8, paddingBottom: 16 },
  separator: { height: 16 },
  headerFrame: { paddingBottom: 16 },
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  summaryCard: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 8,
    padding: 12
  },
  myTableSummaryCard: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Maroon']
  }
})
